import { resolvePhysicalTrayQuantity } from "./physicalTrayQuantity";
import { isTrayBackedWalkInPieceLine, normalizeOrderItemEnums, toCommercialQuantity } from "./orderItemEnums";
import type { Order } from "../../services/ordersApi";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { orderDiscountApprovalsApi, type OrderDiscountInput, type OrderDiscountQuote } from "@/features/discounts/services/orderDiscountApprovalsApi";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTrayDepositEligibility, useWorkAuthorizationStatus } from "../../hooks/useOrders";
import { RefundTrayDepositDialog } from "./RefundTrayDepositDialog";
import { useAuthStore } from "@/features/auth/stores/authStore";

interface ViewOrderDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const getBalanceAmount = (order: Order) => {
  const total = Number(order.totalAmount ?? 0);
  const paid = Number(order.paidAmount ?? 0);
  const computed = total - paid;
  const balance = Number(order.balanceAmount ?? computed);
  return Math.max(balance, 0);
};

const formatDateOrDash = (value?: string | null) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";

  // Treat Unix epoch and year-1 style placeholders as unset dates.
  if (parsed.getTime() <= 0 || parsed.getUTCFullYear() <= 1970) return "-";

  return parsed.toLocaleDateString();
};

const formatQuantityWithUnit = (quantity: number, unit: number, capacity?: number | null, legacyPieces = false) => {
  const isSingular = Number(quantity) === 1;

  const unitLabel = (() => {
    switch (unit) {
      case 1:
        return isSingular ? "Kilogram" : "Kilograms";
      case 2:
        return isSingular ? "Gram" : "Grams";
      case 3:
        return isSingular ? "Liter" : "Liters";
      case 4:
        return isSingular ? "Milliliter" : "Milliliters";
      case 5:
        return isSingular ? "Piece" : "Pieces";
      case 6:
        return isSingular ? "Box" : "Boxes";
      case 7:
        return isSingular ? "Bag" : "Bags";
      case 8:
        return isSingular ? "Packet" : "Packets";
      case 9:
        return isSingular ? "Bottle" : "Bottles";
      case 10:
        return isSingular ? "Can" : "Cans";
      case 11:
        return isSingular ? "Tray" : "Trays";
      default:
        return isSingular ? "Unit" : "Units";
    }
  })();

  const base = `${quantity} ${unitLabel}`;

  // Physical capacity for tray pricing; server sellable metadata for legacy piece lines.
  if (unit === 11 && quantity !== Math.floor(quantity)) {
    const sellablePerTray = capacity;
    if (sellablePerTray) {
      const pieces = legacyPieces ? String(Math.round(quantity * sellablePerTray)) : resolvePhysicalTrayQuantity(String(quantity), sellablePerTray).pieces;
      if (pieces == null) return base;
      return `${base} (${pieces} pcs)`;
    }
  }

  return base;
};

const FULFILLMENT_CONFIG: Record<
  number,
  { label: string; variant: "default" | "secondary" | "outline" | "destructive" }
> = {
  0: { label: "Unknown",       variant: "outline" },
  1: { label: "From Stock",    variant: "default" },
  2: { label: "Partial Stock", variant: "secondary" },
  3: { label: "Needs Sowing",  variant: "destructive" },
  4: { label: "Customer Seeds",variant: "outline" },
  5: { label: "From Expo",     variant: "secondary" },
};

const FulfillmentSourceBadge = ({
  source,
  note,
}: {
  source: number;
  note?: string;
}) => {
  const config = FULFILLMENT_CONFIG[source] ?? FULFILLMENT_CONFIG[0];
  if (source === 0) return null;
  const badge = (
    <Badge variant={config.variant} className="text-xs">
      {config.label}
    </Badge>
  );
  if (!note) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{note}</TooltipContent>
    </Tooltip>
  );
};

/** Build a user-friendly discount label for display */
const getDiscountLabel = (discountSource: string | undefined, discountReference: string | undefined): string => {
  if (!discountSource || discountSource === "None") return "";
  if (discountSource === "Campaign") {
    // discountReference might be like "Campaign Name (2024-01-01 - 2024-12-31)" or just the campaign name
    if (discountReference) {
      const nameMatch = discountReference.match(/^([^(]+)/);
      const campaignName = nameMatch ? nameMatch[1].trim() : discountReference;
      return `Campaign: ${campaignName}`;
    }
    return "Campaign";
  } else if (discountSource === "Individual" || discountSource === "Customer") {
    return "Individual";
  } else if (discountSource === "GroupDefault" || discountSource === "Group") {
    return "Group Default";
  }
  return discountSource;
};

export const ViewOrderDialog = ({
  order: rawOrder,
  open,
  onOpenChange,
}: ViewOrderDialogProps) => {
  const order = useMemo(() => normalizeOrderItemEnums(rawOrder), [rawOrder]);
  const formatOrderItemQuantity = (item: Order["orderItems"][number], quantity: number) => {
    if (isTrayBackedWalkInPieceLine(order, item)) {
      const pieces = toCommercialQuantity(order, item, quantity);
      const stockNote = quantity > 0 ? ` (${quantity} tray stock)` : "";
      return `${pieces} ${pieces === 1 ? "Piece" : "Pieces"}${stockNote}`;
    }
    return formatQuantityWithUnit(
      quantity, item.unit, item.pricingMode === 2 ? item.sellablePerTray : item.trayCapacity,
      item.pricingMode === 2,
    );
  };
  const totalAmount = Number(order.totalAmount ?? 0);
  const paidAmount = Number(order.paidAmount ?? 0);
  const balanceAmount = getBalanceAmount(order);
  const paidPct = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;
  const workAuthQuery = useWorkAuthorizationStatus(order.id, open);
  const eligibilityQuery = useTrayDepositEligibility(order.id, open && !!order.includesTrayDeposit);
  const canRefundTrayDeposit = useAuthStore((state) => state.hasPermission("orders.update"));
  const canRequestDiscount = useAuthStore((state) => state.hasPermission("discounts.request"));
  const queryClient = useQueryClient();
  const [specialKind, setSpecialKind] = useState<OrderDiscountInput["kind"]>("Percentage");
  const [specialValue, setSpecialValue] = useState("");
  const [specialReason, setSpecialReason] = useState("");
  const [specialQuote, setSpecialQuote] = useState<OrderDiscountQuote | null>(null);
  const discountRequests = useQuery({
    queryKey: ["order-discount-requests", order.id],
    queryFn: () => orderDiscountApprovalsApi.forOrder(order.id),
    enabled: open,
  });
  const pendingDiscount = discountRequests.data?.items.find(request => request.status === "PendingApproval" || request.status === "Draft");
  const quoteDiscount = useMutation({
    mutationFn: (input: OrderDiscountInput) => orderDiscountApprovalsApi.quote(order.id, input),
    onSuccess: setSpecialQuote,
    onError: () => { setSpecialQuote(null); toast.error("Unable to quote this discount. Check the value and try again."); },
  });
  const submitDiscount = useMutation({
    mutationFn: (input: OrderDiscountInput) => orderDiscountApprovalsApi.submit(order.id, input),
    onSuccess: async () => {
      toast.success("Special discount sent to the GM for approval.");
      setSpecialQuote(null);
      setSpecialValue("");
      setSpecialReason("");
      await queryClient.invalidateQueries({ queryKey: ["order-discount-requests", order.id] });
    },
    onError: () => toast.error("Unable to submit discount request. Refresh the order and try again."),
  });
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const trayDepositAmount = Number(order.trayDepositAmount ?? 0);
  const trayDepositRefunded = Number(order.trayDepositRefunded ?? 0);
  const eligibility = eligibilityQuery.data;
  const trayDepositRefundable = order.includesTrayDeposit
    && trayDepositAmount - trayDepositRefunded > 0
    && Number(eligibility?.eligibleReturnedQuantity ?? 0) > 0
    && (Number(eligibility?.availableQuantity210Hole ?? 0) > 0
      || Number(eligibility?.availableQuantity442Hole ?? 0) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 w-[95vw] sm:!max-w-[1400px]" >
        <DialogHeader>
          <DialogTitle>Order Details</DialogTitle>
          <DialogDescription>
            {order.customerName} - {order.orderNumber || order.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Status */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge>{order.status}</Badge>
                {order.fulfillmentSummary && (
                  <Badge
                    variant={
                      order.fulfillmentSummary === "FullyFulfilled"
                        ? "default"
                        : order.fulfillmentSummary === "PartiallyFulfilled"
                          ? "secondary"
                          : "outline"
                    }
                    className={
                      order.fulfillmentSummary === "FullyFulfilled"
                        ? "bg-emerald-600 hover:bg-emerald-700"
                        : order.fulfillmentSummary === "PartiallyFulfilled"
                          ? "bg-amber-100 text-amber-900 hover:bg-amber-100"
                          : ""
                    }
                  >
                    {order.fulfillmentSummary === "FullyFulfilled"
                      ? "Fully Fulfilled"
                      : order.fulfillmentSummary === "PartiallyFulfilled"
                        ? "Partially Fulfilled"
                        : "Awaiting Fulfilment"}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created By</p>
              <p className="font-medium text-sm">{order.createdBy || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created At</p>
              <p className="font-medium text-sm">
                {formatDateOrDash(order.createdAt)}
              </p>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Payment Summary</h3>
            {pendingDiscount && <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <strong>Special discount {pendingDiscount.status === "Draft" ? "draft" : "pending GM approval"}</strong>
              <p>Current approved price remains {formatCurrency(Number(order.totalAmount ?? 0))}. Proposed goods payable: {formatCurrency(pendingDiscount.proposedGoodsPayable)}.</p>
              <p>Additional reduction if approved: {formatCurrency(pendingDiscount.incrementalReduction)}.</p>
            </div>}
            {canRequestDiscount && !pendingDiscount && <div className="mb-4 space-y-2 rounded-md border p-3 text-sm">
              <p className="font-medium">Request Special Discount</p>
              <p className="text-muted-foreground">The negotiated discount replaces the existing one after GM approval.</p>
              <div className="flex flex-wrap gap-2">
                <Select value={specialKind} onValueChange={value => { setSpecialKind(value as OrderDiscountInput["kind"]); setSpecialQuote(null); }}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Percentage">Percentage</SelectItem><SelectItem value="FixedAmount">Fixed amount</SelectItem></SelectContent>
                </Select>
                <Input aria-label="Requested discount value" type="number" min="0" step="0.01" className="w-40" value={specialValue}
                  onChange={event => { setSpecialValue(event.target.value); setSpecialQuote(null); }} />
              </div>
              <Textarea aria-label="Discount reason" placeholder="Reason for the negotiated discount" value={specialReason}
                onChange={event => { setSpecialReason(event.target.value); setSpecialQuote(null); }} />
              <Button type="button" size="sm" variant="outline" disabled={quoteDiscount.isPending || !specialReason.trim() || Number(specialValue) <= 0}
                onClick={() => quoteDiscount.mutate({ kind: specialKind, value: Number(specialValue), reason: specialReason })}>Preview</Button>
              {specialQuote && <div className="space-y-1 rounded bg-muted p-3">
                <p>Goods subtotal: {formatCurrency(specialQuote.goodsSubtotal)}</p>
                <p>Current payable goods: {formatCurrency(specialQuote.currentGoodsPayable)}</p>
                <p>Proposed payable goods: {formatCurrency(specialQuote.proposedGoodsPayable)}</p>
                <p>Additional reduction: {formatCurrency(specialQuote.incrementalReduction)}</p>
                {specialKind === "FixedAmount" && <p>Equivalent rate: {specialQuote.equivalentPercentage.toFixed(2)}%</p>}
                <Button type="button" size="sm" disabled={submitDiscount.isPending} onClick={() => submitDiscount.mutate({ kind: specialKind, value: Number(specialValue), reason: specialReason })}>Send for GM approval</Button>
              </div>}
            </div>}
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
              {Number(order.discountAmount ?? 0) > 0 && (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground">Original (goods)</p>
                    <p className="font-medium text-sm">
                      {formatCurrency(Number(order.originalAmount ?? 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Discount{(() => {
                        const label = getDiscountLabel(order.discountSource, order.discountReference);
                        return label ? ` (${label})` : "";
                      })()}
                    </p>
                    <p className="font-medium text-sm text-emerald-700">
                      −{formatCurrency(Number(order.discountAmount ?? 0))}
                      {Number(order.discountPercentage ?? 0) > 0
                        ? ` (${Number(order.discountPercentage).toFixed(2)}%)`
                        : ""}
                    </p>
                  </div>
                </>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="font-medium text-sm">{formatCurrency(totalAmount)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paid</p>
                <p className="font-medium text-sm text-emerald-700">
                  {formatCurrency(paidAmount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Balance</p>
                <p
                  className={`font-medium text-sm ${
                    balanceAmount > 0 ? "text-amber-700" : "text-emerald-700"
                  }`}
                >
                  {formatCurrency(balanceAmount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment</p>
                <p className="font-medium text-sm">
                  {paidPct.toFixed(0)}% ({order.paymentStatus || (balanceAmount > 0 ? "Pending" : "Paid")})
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Work Authorization</p>
                <p className="font-medium text-sm">
                  {workAuthQuery.data?.status === "PaymentThresholdSatisfied"
                    ? "Auto-authorized by payment threshold"
                    : workAuthQuery.data?.status === "Approved"
                      ? "Approved by General Manager"
                      : workAuthQuery.data?.status ?? "Not requested"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Due Date</p>
                <p className="font-medium text-sm">
                  {formatDateOrDash(order.paymentDueDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Invoice Reference</p>
                <p className="font-medium text-sm break-all">
                  {order.invoiceReference || "-"}
                </p>
              </div>
              {order.includesTrayDeposit && (
                <div>
                  <p className="text-sm text-muted-foreground">Tray Deposit</p>
                  <p className="font-medium text-sm">
                    {formatCurrency(trayDepositAmount)}{" "}
                    <span className="text-muted-foreground">
                      ({formatCurrency(trayDepositRefunded)} refunded / reserved,{" "}
                      {formatCurrency(Math.max(0, trayDepositAmount - trayDepositRefunded))}{" "}
                      refundable)
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {eligibilityQuery.isLoading
                      ? "Checking approved tray returns…"
                      : `${eligibility?.approvedReturnedQuantity ?? 0} approved returned · ${eligibility?.eligibleReturnedQuantity ?? 0} unclaimed`}
                  </p>
                </div>
              )}
            </div>
            {trayDepositRefundable && canRefundTrayDeposit && (
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setRefundDialogOpen(true)}>
                  Refund Deposit
                </Button>
              </div>
            )}
          </div>

          {/* Delivery Information */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Delivery Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium text-sm">{order.deliveryLocation}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Transport</p>
                {order.transportCost != null ? (
                  <p className="font-medium text-sm">
                    {order.transportDestinationName ?? "—"}{" "}
                    <span className="text-muted-foreground">
                      (KES {order.transportCost.toLocaleString("en-KE", { minimumFractionDigits: 2 })})
                    </span>
                  </p>
                ) : (
                  <p className="font-medium text-sm text-muted-foreground">Customer collects</p>
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contact Person</p>
                <p className="font-medium text-sm">
                  {order.deliveryContactPerson}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contact Phone</p>
                <p className="font-medium text-sm">
                  {order.deliveryContactPhone}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Expected Delivery
                </p>
                <p className="font-medium text-sm">
                  {formatDateOrDash(order.expectedDeliveryDate)}
                </p>
              </div>
            </div>
            {order.deliveryInstructions && (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground mb-1">
                  Instructions
                </p>
                <p className="text-sm bg-muted p-2 rounded">
                  {order.deliveryInstructions}
                </p>
              </div>
            )}
          </div>

          {/* Planting Information */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Planting Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <div>
                <p className="text-sm text-muted-foreground">
                  Planned Planting Date
                </p>
                <p className="font-medium text-sm">
                  {formatDateOrDash(order.plannedPlantingDate)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Requested Production Stage
                </p>
                <p className="font-medium text-sm">
                  {order.requestedProductionStage || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-3">Order Items</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Supply status shows how much of each item has been handed over. It is separate from payment status.
            </p>
            <div className="rounded border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Variety</TableHead>
                    <TableHead>Ordered</TableHead>
                    <TableHead>Supplied</TableHead>
                    <TableHead>Remaining to supply</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Stock Source</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {order.orderItems.map((item, idx) => {
                    const lineStatus = item.isFullyFulfilled
                      ? "Fulfilled"
                      : item.quantityFulfilled > 0
                        ? "In Progress"
                        : "Awaiting supply";
                    return (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">
                          {item.variety || item.productName || item.cropType || "-"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatOrderItemQuantity(item, item.quantity)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatOrderItemQuantity(item, item.quantityFulfilled)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatOrderItemQuantity(item, item.quantityPending)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              lineStatus === "Fulfilled"
                                ? "default"
                                : lineStatus === "In Progress"
                                  ? "secondary"
                                  : "outline"
                            }
                            className={
                              lineStatus === "Fulfilled"
                                ? "bg-emerald-600 hover:bg-emerald-700"
                                : lineStatus === "In Progress"
                                  ? "bg-amber-100 text-amber-900 hover:bg-amber-100"
                                  : ""
                            }
                          >
                            {lineStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {item.isCompanySeedling ? "Company" : "Farmer"}
                        </TableCell>
                        <TableCell>
                          <FulfillmentSourceBadge
                            source={item.fulfillmentSource}
                            note={item.stockAdvisoryNote}
                          />
                        </TableCell>
                        <TableCell className="text-sm break-words max-w-xs">
                          {item.notes || "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Special Instructions */}
          {order.specialInstructions && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Special Instructions</h3>
              <p className="text-sm bg-muted p-2 rounded">
                {order.specialInstructions}
              </p>
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Notes</h3>
              <p className="text-sm bg-muted p-2 rounded">{order.notes}</p>
            </div>
          )}
        </div>

        {eligibility && (
          <RefundTrayDepositDialog
            order={order}
            eligibility={eligibility}
            open={refundDialogOpen}
            onOpenChange={setRefundDialogOpen}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
